import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import useFetch from "../server/useFetch";
// import { useTranslation } from "react-i18next";
import { useTranslation } from "react-i18next";
import query from "../server/constants.api";
import logger from "../server/server";
import { isLogItem } from "../utils/hooks/logItems";
import { useAuthContext } from "./AuthContext";
import { getTenantConfig } from "../utils/config/tenantConfig";

const GlobalFilterContext = createContext(null);

export const useGlobalFilterContext = () => {
  const context = useContext(GlobalFilterContext);

  if (!context) {
    throw new Error(
      "useGlobalFilterContext must be used within an ContentProvider"
    );
  }
  return context;
};

export const GlobalFilterProvider = (props) => {
  const tenantConfig = getTenantConfig();

  const { t } = useTranslation();
  const { user, tenant } = useAuthContext();
  const [isToApplyFilters, setIsToApplyFilters] = useState(false); //Triggers certain components to apply filters
  const [dropdownEntity, setDropdownEntity] = useState(null); //Dropdown options
  const [entity, setEntity] = useState(null); //Dropdown value
  const [dropdownSubEntity, setDropdownSubEntity] = useState(null); //Dropdown options
  const [subEntity, setSubEntity] = useState(null); //Dropdown value
  // const [copySubEntity, setCopySubEntity] = useState(null);
  const [dataRegions, setDataRegions] = useState(null); //Dropdown value
  // const [itemAll, setItemAll] = useState(null);

  const [mapPois, setMapPois] = useState(null);
  const [copyMapPois, setCopyMapPois] = useState(null);
  const [dropdownClasses, setDropdownClasses] = useState([]);
  const [classes, setClasses] = useState(null);
  const [dropdownSeverities, setDropdownSeverities] = useState([]);
  const [severitiesSelected, setSeveritiesSelected] = useState(null);
  const [dropdownImpactedFA, setDropdownImpactedFA] = useState([]);
  const [impactedFASelected, setImpactedFASelected] = useState(null);
  const [dropdownFAOwner, setDropdownFAOwner] = useState([]);
  const [faOwnerSelected, setFAOwnerSelected] = useState(null);
  const [dropdownVenueType, setDropdownVenueType] = useState([]);
  const [venueTypeSelected, setVenueTypeSelected] = useState(null);
  const [dropdownsVenues, setDropdownsVenues] = useState([]);
  const [venuesSelected, setVenuesSelected] = useState(null);
  const [dropdownConfidential, setDropdownConfidential] = useState([]);
  const [confidentialSelected, setConfidentialSelected] = useState(null);

  const [selectedLayer, setSelectedLayer] = useState('Incidents');//Define o layer a apresentar no mapa

  const { data: dataRegionsDropdown, refetch: refetchRegionsDropdown, isFetching: dataRegionsIsFetching } = useFetch("/map/regions", "regionDropdown",
    {
      tenant: tenant,
    }, false, null, query.middlewareURL);

  const { data: mapItemsIncidentsData, isFetching: isFetchingMapIncidentsData, refetch: refetchMapItemsIncidents, remove: removeMapItemsIncidents } = useFetch(
    '/incidents/map/items', 'getMapItemsIncidents',
    {
      id: subEntity ? subEntity?.id : entity?.id,
      // layer: "INCIDENTS",
      tenant: subEntity ? subEntity?.tenant : entity?.tenant
    },
    subEntity ? true : entity ? true : false, null, query.middlewareCommandURL);

  const { data: mapItemsEventsData, isFetching: isFetchingMapItemsEventsData, refetch: refetchMapItemsEvents, remove: removeMapItemsEvents } = useFetch(
    query.path.mapItems,
    'getMapItemsEvents',
    {
      layer: "logItem",
      tenant: subEntity ? subEntity?.tenant : entity?.tenant
    },
    subEntity ? true : entity ? true : false, null, query.middlewareURL);

  useEffect(() => {
    removeMapItemsIncidents();
    removeMapItemsEvents();
  }, [user]);

  useEffect(() => {
    if (subEntity) {
      refetchMapItemsIncidents();
      refetchMapItemsEvents();
    }
  }, [subEntity, user]);

  /**
   * * @description This function is used to filter the map items based on the selected filters.
   */
  useEffect(() => {
    if (!mapPois) return;
    let filtered = [...mapPois];

    const apply = (selected, fnFilter) => {
      if (selected === null || selected === undefined) {
        return;
      }

      filtered = filtered.filter(fnFilter);
    };

    // 1) classes
    apply(classes, item => {
      const vals = classes.map(x => x.value);
      return vals.includes(item.class);
    });

    // 2) severities
    apply(severitiesSelected, item => {
      if (isLogItem(item)) return true;

      const vals = severitiesSelected.map(x => x.value);
      return severitiesSelected.some(sel =>
        sel.value === 'N/A'
          ? !item.severity
          : vals.includes(item.severity)
      );
    });

    // 3) impactedFA
    apply(impactedFASelected, item => {
      const parts = item.impactedFA?.trim() ? item.impactedFA.split(', ') : [];
      return impactedFASelected.some(sel =>
        sel.value === 'N/A'
          ? parts.length === 0
          : parts.includes(sel.value)
      );
    });

    // 4) FA owner
    apply(faOwnerSelected, item => {
      const vals = faOwnerSelected.map(x => x.value);
      return vals.includes(item.issueOwnerGroup);
    });

    // 5) venueType
    apply(venueTypeSelected, item => {
      return venueTypeSelected.some(sel =>
        sel.value === 'Competition'
          ? item.locationCompetition === true
          : sel.value === 'Non-Competition'
            ? item.locationCompetition === false
            : false
      );
    });

    // 6) venues
    apply(venuesSelected, item => {
      const vals = venuesSelected.map(x => x.value);
      return vals.includes(item.serviceName);
    });

    // 7) confidentiality
    apply(confidentialSelected, item => {
      if (isLogItem(item)) return true;

      return confidentialSelected.some(sel =>
        sel.value === 'Confidential'
          ? item.confidential === "true"
          : sel.value === 'Non-Confidential'
            ? item.confidential === "false"
            : false
      );
    });

    setCopyMapPois(filtered);
  }, [
    mapPois,
    classes,
    severitiesSelected,
    impactedFASelected,
    faOwnerSelected,
    venueTypeSelected,
    venuesSelected,
    confidentialSelected,
  ]);

  useEffect(() => {
    if (tenant) {
      refetchRegionsDropdown();
    }
  }, [tenant])

  useEffect(() => {
    setDataRegions(dataRegionsDropdown);
  }, [dataRegionsDropdown]);

  useEffect(() => {
    if (dataRegions) {
      const filterObj = {};

      const dropdownCountriesObject = selectSerialize(dataRegions);
      setDropdownEntity(dropdownCountriesObject);
      setEntity(dropdownCountriesObject[0]);
      filterObj.entity = dropdownCountriesObject[0];

      if (dropdownCountriesObject[0]?.cities) {
        const dropdownSubEntityArray = selectSerialize(
          dropdownCountriesObject[0]?.cities
        );
        const allItem = {//Criado para vermos o conjunto de todos os CLusters (Zonas)
          value: 'All Clusters',
          label: t('All Clusters'),
          id: dropdownCountriesObject[0]?.id,
          tenant: tenant,
          latitude: tenantConfig.latitude,
          longitude: tenantConfig.longitude,
          zoom: tenantConfig.zoom,
        };
        const newCitiesArray = [allItem, ...dropdownSubEntityArray];

        setDropdownSubEntity(newCitiesArray);
        setSubEntity(newCitiesArray[0]);
        filterObj.subEntity = newCitiesArray[0];
      }

      // setIsToApplyFilters(true);

      if (Object.keys(dataRegions).length === 0) {
        logger.warn("User: " + user?.Username + " Tenant: " + tenant + ", dataRegions is a empty object!");
      }
    }
  }, [dataRegions]);

  useEffect(() => {
    if (mapItemsIncidentsData || mapItemsEventsData) {
      const incidentsData = mapItemsIncidentsData?.filter(item =>
        item.latitude && item.latitude !== "0"
        && item.longitude && item.longitude !== "0"
        && item.severity !== '1 - Very Low' &&
        !item.family?.includes('Facilities') &&
        !item.confidential?.includes('true') &&
        item.class) || [];

      const servicesData = mapItemsEventsData?.filter(item =>
        item.latitude && item.latitude !== "0" &&
        item.longitude && item.longitude !== "0" &&
        item.class
      ) || [];

      const data = [...incidentsData, ...servicesData];

      setMapPois(data);
      setCopyMapPois(data);

      //Classes
      const poiClasses = data?.map((x) => {
        return { label: x.class, value: x.class };
      });
      const uniqueClassesArray = poiClasses?.filter((item, index, self) =>
        index === self.findIndex((t) => t.value === item.value)
      );//Limpar duplicados
      setClasses(uniqueClassesArray);
      setDropdownClasses(uniqueClassesArray);

      // if (selectedLayer === 'Incidents') {
      //Severity
      const incidentsLevels = data
        ?.filter((x => !isLogItem(x)))
        .map((x) => {
          if (!x.severity) return { label: 'N/A', value: 'N/A' }
          return { label: x.severity, value: x.severity };
        });
      const uniqueIncidentsLevels = incidentsLevels?.filter((item, index, self) =>
        index === self.findIndex((t) => t.value === item.value)
      ).sort((a, b) => {
        const numA = parseInt(a.value.split(' - ')[0]);
        const numB = parseInt(b.value.split(' - ')[0]);
        return numA - numB;
      });

      setSeveritiesSelected(uniqueIncidentsLevels);
      setDropdownSeverities(uniqueIncidentsLevels);

      //Impacted FA
      const impactedFAArray = [];
      data?.map((x) => {
        if (typeof x.impactedFA === 'string') {
          const values = x.impactedFA.split(', ');
          values.forEach((value) => {
            impactedFAArray.push(value);
          });
        }
      })
      const uniqueImpactedFAArray = impactedFAArray?.filter((item, index, self) =>
        index === self.findIndex((t) => t === item)
      ).map(x => {
        if (x === '') return { label: 'N/A', value: 'N/A' }
        return { label: x, value: x }
      }); // Remove duplicates
      setImpactedFASelected(uniqueImpactedFAArray);
      setDropdownImpactedFA(uniqueImpactedFAArray);

      //FA Owner
      const faOwners = data?.map((x) => {
        return { label: x.issueOwnerGroup, value: x.issueOwnerGroup };
      });
      const uniqueFAOwners = faOwners?.filter((item, index, self) =>
        index === self.findIndex((t) => t.value === item.value)
      );//Limpar duplicados
      setFAOwnerSelected(uniqueFAOwners);
      setDropdownFAOwner(uniqueFAOwners);
      // };

      //Venue Type
      const venueTypeArray = [{ value: 'Competition', label: 'Competition' }, { value: 'Non-Competition', label: 'Non-Competition' }];
      if (data.length > 0) {
        setDropdownVenueType(venueTypeArray);
        setVenueTypeSelected(venueTypeArray);
      } else {
        setDropdownVenueType(null);
        setVenueTypeSelected(null);
      }

      //Venues
      const venuesArray = data?.map((x) => {
        return { label: x.serviceName, value: x.serviceName };
      });
      const uniqueVenuesArray = venuesArray?.filter((item, index, self) => {
        return index === self.findIndex((t) => t.value === item.value)
      });
      setVenuesSelected(uniqueVenuesArray);
      setDropdownsVenues(uniqueVenuesArray);

      //Confidentials
      const confidentialArray = [{ value: 'Confidential', label: 'Confidential' }, { value: 'Non-Confidential', label: 'Non-Confidential' }];
      if (mapItemsIncidentsData?.length > 0) {
        setDropdownConfidential(confidentialArray);
        setConfidentialSelected(confidentialArray);
      } else {
        setDropdownConfidential(null);
        setConfidentialSelected(null);
      }

    };
  }, [mapItemsIncidentsData, mapItemsEventsData]);

  const selectEntity = (value) => {
    if (value?.value) {
      const cities = dataRegions?.find((x) => {
        const name = x.name.split(".");
        if (name[0] === value.value) {
          return x;
        }
      })?.cities;

      if (cities) {
        const dropdownSubEntityObject = selectSerialize(cities);
        setDropdownSubEntity(dropdownSubEntityObject);
        setSubEntity(dropdownSubEntityObject[0]);
      } else {
        setDropdownSubEntity(null);
        setSubEntity(null);
      }
      setEntity(value);
    }
  };

  const value = useMemo(() => {
    return {
      isToApplyFilters,
      setIsToApplyFilters,

      dropdownEntity,
      entity,
      selectEntity,

      dropdownSubEntity,
      subEntity,
      setSubEntity,

      dataRegionsIsFetching,

      dropdownClasses,
      setDropdownClasses,
      classes,
      setClasses,
      dropdownSeverities,
      setDropdownSeverities,
      severitiesSelected,
      setSeveritiesSelected,
      dropdownImpactedFA,
      impactedFASelected,
      setImpactedFASelected,
      dropdownFAOwner,
      faOwnerSelected,
      setFAOwnerSelected,
      dropdownVenueType,
      venueTypeSelected,
      setVenueTypeSelected,
      dropdownsVenues,
      venuesSelected,
      setVenuesSelected,
      dropdownConfidential,
      confidentialSelected,
      setConfidentialSelected,

      selectedLayer,
      setSelectedLayer,

      mapPois,
      copyMapPois,
      setCopyMapPois,

      mapItemsIncidentsData,
      isFetchingMapIncidentsData,
      refetchMapItemsIncidents,

      // mapItemsServicesData,
      // isFetchingMapServicesData,
      // refetchMapItemsServices,
    };
  }, [entity, subEntity, isToApplyFilters, dataRegionsIsFetching, mapItemsIncidentsData, isFetchingMapIncidentsData,
    // mapItemsServicesData, isFetchingMapServicesData, 
    dropdownClasses, classes,
    dropdownSeverities, severitiesSelected,
    dropdownImpactedFA, impactedFASelected,
    dropdownVenueType, venueTypeSelected,
    dropdownsVenues, venuesSelected,
    dropdownConfidential, confidentialSelected,
    copyMapPois,
    selectedLayer, setSelectedLayer,
  ]);

  return (
    <GlobalFilterContext.Provider value={value}>
      {props.children}
    </GlobalFilterContext.Provider>
  );
};

const selectSerialize = (array) => {
  return array?.sort((a, b) => b.buIndex - a.buIndex)?.map((x) => {
    const name = x.name.split(".");
    return {
      value: name[0],
      label: name[0],
      id: x.id,
      tenant: x.tenant,
      cities: x.cities,
      latitude: x.latitude,
      longitude: x.longitude,
      zoom: x.zoom,
    };
  });
};